/**
 * ============================================================================
 * TRANSIENT: Garantie Annulation Vol (flight-cancellation-coverage)
 * Type: campaign
 * Amplitude key: none
 * End date: 2027-06-30
 * PR: #2885
 * ============================================================================
 *
 * Temporary badge shown on dossiers eligible to the "Garantie Annulation Vol"
 * offer (flight cancellation coverage). Gated by the API field
 * `dossier.flightCancellationCoverageEligible` — no feature flag.
 *
 * Mutually exclusive with Spring Flex by construction (disjoint booking windows);
 * each badge depends only on its own flag, no cross-exclusion is enforced.
 *
 * Sunsets once the offer ends — delete this folder in one PR.
 * Ticket: BBIZ-624
 */

import {
    Chip,
    Link,
    paletteColors,
    Stack,
    styled,
    Tooltip,
    tooltipClasses,
    TooltipProps,
    Typography,
} from '@evaneos/b2b-ui';
import { useIntl } from 'react-intl';

import { useGetDossierData } from '../../resources/dossier/queries';
import { DossierId } from '../../resources/dossier/types';

const LEARN_MORE_URL =
    'https://go.meltingspot.io/spot/73df679f-6579-4937-83c0-70212b61a9d2/documents/ac29ebe8-71f2-429b-9582-85f8189c2c0e?from=24ce86f5-1c0c-4125-8795-145afab66c2a';

const FlightCancellationTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: 320,
        backgroundColor: paletteColors.neutral[900],
        padding: 12,
    },
    [`& .${tooltipClasses.arrow}`]: {
        color: paletteColors.neutral[900],
    },
});

type Props = {
    dossierId: DossierId;
};

export function FlightCancellationBadge({ dossierId }: Readonly<Props>) {
    const { formatMessage } = useIntl();
    const dossierData = useGetDossierData(dossierId);

    if (!dossierData?.dossier?.flightCancellationCoverageEligible) {
        return null;
    }

    return (
        <FlightCancellationTooltip
            arrow
            placement="bottom-start"
            title={
                <Stack gap={1}>
                    <Typography variant="subtitle2" fontWeight={700} color="common.white">
                        {formatMessage({ id: 'flight_coverage.popover.title' })}
                    </Typography>
                    <Typography variant="body2" color="common.white">
                        {formatMessage({ id: 'flight_coverage.popover.body' })}
                    </Typography>
                    <Link
                        href={LEARN_MORE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="always"
                        color="inherit"
                        sx={{
                            fontWeight: 700,
                            color: 'common.white',
                            '&:hover': { color: paletteColors.neutral[300] },
                        }}
                    >
                        {formatMessage({ id: 'flight_coverage.popover.learn_more' })}
                    </Link>
                </Stack>
            }
        >
            <Chip
                data-spec-id="flight-coverage-badge"
                label={formatMessage({ id: 'flight_coverage.badge.label' })}
                sx={{
                    backgroundColor: paletteColors.green[100],
                    color: paletteColors.green[800],
                    fontWeight: 700,
                    borderRadius: 64,
                }}
            />
        </FlightCancellationTooltip>
    );
}
